import { customAlphabet } from 'nanoid';
import { get } from '../../helpers/utils/http-client';
import { API_PREFIX } from '../../consts/api-endpoints';
import { createBotMessagePayload, createSelfMessagePayload, mergeBotMessage } from '../../helpers/utils/message-utils';

const newId = customAlphabet('0123456789abcdefghijklmnopqrstuvwxyz', 24);
const fallback = new Map();
const scopeKey = (appId, userId) => `bc-widget:${encodeURIComponent(appId)}:${encodeURIComponent(userId)}:sessions`;

function read(key) {
  try { return window.localStorage.getItem(key) || fallback.get(key); }
  catch (_) { return fallback.get(key); }
}
function write(key, value) {
  try {
    window.localStorage.setItem(key, value);
    fallback.delete(key);
  } catch (_) {
    // Storage may be blocked or full in an embedded widget.
    fallback.set(key, value);
  }
}

export function getWidgetUserId(appId, suppliedUserId) {
  const key = `bc-widget:${encodeURIComponent(appId)}:userId`;
  const supplied = suppliedUserId == null ? '' : String(suppliedUserId).trim();
  // Explicit host identities have separate indexes and do not replace the anonymous identity.
  if (supplied) return supplied;
  const userId = read(key) || newId();
  write(key, userId);
  return userId;
}

export function readSessions(appId, userId) {
  try {
    const data = JSON.parse(read(scopeKey(appId, userId)) || '{}');
    return {
      activeSessionId: typeof data.activeSessionId === 'string' ? data.activeSessionId : null,
      sessions: Array.isArray(data.sessions) ? data.sessions.filter(item => item && typeof item.sessionId === 'string') : [],
    };
  } catch (_) { return { activeSessionId: null, sessions: [] }; }
}

export function saveSessions(appId, userId, data) {
  write(scopeKey(appId, userId), JSON.stringify(data));
}

/**
 * The user's sessions as the backend recorded them (any device), newest first.
 * Resolves to null when the list is unavailable - e.g. a backend without this
 * endpoint - so callers can fall back to the local index.
 */
export async function listUserSessions(appId, userId, accessKey, limit = 50) {
  const { response, status } = await get(
    `${API_PREFIX}/apps/${encodeURIComponent(appId)}/users/${encodeURIComponent(userId)}/sessions`,
    { pageSize: limit },
    { Authorization: accessKey }
  );
  if (status !== 200 || !Array.isArray(response?.data)) return null;
  return response.data.filter(item => item && typeof item.sessionId === 'string').map(item => ({
    sessionId: item.sessionId,
    title: typeof item.displayMessage === 'string' && item.displayMessage ? item.displayMessage.slice(0, 80) : '',
    createdAt: item.createdAt,
    updatedAt: item.modifiedAt || item.createdAt,
  }));
}

/**
 * Union of the local index and the server list. Local titles win (they are the
 * first thing the user typed; the server only keeps the latest message), the
 * newest timestamp wins, and sessions only the server knows are appended.
 */
export function mergeSessions(local, remote) {
  const byId = new Map(local.map(item => [item.sessionId, item]));
  remote.forEach(item => {
    const existing = byId.get(item.sessionId);
    if (!existing) { byId.set(item.sessionId, { ...item, title: item.title || 'New chat' }); return; }
    const keepLocalTitle = existing.title && existing.title !== 'New chat';
    byId.set(item.sessionId, {
      ...existing,
      title: keepLocalTitle ? existing.title : item.title || existing.title || 'New chat',
      createdAt: existing.createdAt || item.createdAt,
      updatedAt: [existing.updatedAt, item.updatedAt].filter(Boolean).sort().pop(),
    });
  });
  return [...byId.values()].sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')));
}

export async function loadSessionMessages(appId, sessionId, accessKey) {
  let messages = [];
  let cursor;
  const seen = new Set();
  do {
    const { response, status } = await get(
      `${API_PREFIX}/apps/${encodeURIComponent(appId)}/sessions/${encodeURIComponent(sessionId)}/messages`,
      { pageSize: 30, ...(cursor ? { requestPage: cursor } : {}) },
      { Authorization: accessKey }
    );
    if (status !== 200 || !Array.isArray(response?.data)) throw new Error('Could not load this conversation. Please try again.');
    response.data.forEach(entry => {
      if (!entry?.message) return;
      const message = entry.message;
      if (entry.sentBy === 'bot') messages = mergeBotMessage(messages, createBotMessagePayload(typeof message === 'string' ? { type: 'TEXT', content: message } : message));
      else messages.push(createSelfMessagePayload(typeof message === 'string' ? message : message.content));
    });
    cursor = response.pagination?.nextPage;
    if (cursor && seen.has(cursor)) throw new Error('Could not load the complete conversation. Please try again.');
    seen.add(cursor);
  } while (cursor);
  return messages;
}
